/**
 * HVP Cascade Controller - focus peek-ahead
 *
 * Loads options filtered by ABOVE selections only when a dropdown is
 * focused, with mousedown/focus debounce and stale-response guarding.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeFocusMethods = {
    /**
     * Handle dropdown focus event for Smart Cascade
     *
     * When a user focuses on a dropdown, load options filtered by
     * selections ABOVE this attribute only. This allows the user to see
     * all valid options for this level regardless of what's selected below.
     *
     * This is a key UX improvement - users can explore options at any level
     * without being constrained by selections made further down the cascade.
     *
     * @param {FocusEvent|MouseEvent} event - Focus or mousedown event
     * @fires optionsLoading - When starting to load options
     * @fires optionsLoaded - When options finish loading
     */
    async handleDropdownFocus(event) {
        const select = event.target;
        const attrName = select.dataset.attribute_name;
        const eventType = event.type;

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] DROPDOWN FOCUS/MOUSEDOWN EVENT');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Event type:', eventType);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Attribute:', attrName);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Smart Cascade Enabled:', this.smartCascadeEnabled);

        // Skip if Smart Cascade is disabled
        if (!this.smartCascadeEnabled) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SKIPPED: Smart Cascade is disabled');
            return;
        }

        if (!attrName) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SKIPPED: No attribute name');
            return;
        }

        const normalized = this.normalizeAttributeName(attrName);
        const index = this.getAttributeIndex(normalized);

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Normalized:', normalized);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Index:', index);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Attribute Order:', this.attributeOrder);

        // Debounce: Prevent double-trigger from mousedown + focus on same dropdown
        const now = Date.now();
        const lastFocusTime = select._hvpLastFocusTime || 0;
        const lastFocusAttr = this._lastFocusedAttribute || null;

        if (lastFocusAttr === normalized && (now - lastFocusTime) < 200) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SKIPPED: Debounced (same dropdown within 200ms)');
            return;
        }

        select._hvpLastFocusTime = now;
        this._lastFocusedAttribute = normalized;

        // Skip for first attribute (no "above" selections)
        if (index <= 0) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SKIPPED: First attribute (no above selections)');
            return;
        }

        // Get ALL selections above this dropdown
        const selectionsAbove = this.getSelectionsAbove(index);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Selections ABOVE index', index, ':', JSON.stringify(selectionsAbove));

        // Get ALL current selections (for comparison)
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ALL current selections:', JSON.stringify(this.selections));

        // If no selections above, load ALL options for this attribute (no filtering)
        if (Object.keys(selectionsAbove).length === 0) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] No selections above - loading ALL options for this attribute');

            // Load ALL options (prefetched or from cache)
            const allOptionsKey = this.cache.buildKey(this.productId, {}, normalized);
            const allOptions = this.cache.get(allOptionsKey);

            if (allOptions) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Using cached ALL options:', allOptions.length);
                this.updateDropdownOptions(normalized, allOptions, false);
                return;
            }

            // If no cached all-options, use client cascade with empty selections
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                const options = this.clientCascade.getOptions({}, normalized);
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Client cascade ALL options:', options.length);
                this.cache.set(allOptionsKey, options);
                this.updateDropdownOptions(normalized, options, false);
                return;
            }

            // Otherwise, keep current options (no change)
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] No cached all-options available');
            return;
        }

        // Check cache first using ONLY above selections (this is key!)
        const cacheKey = this.cache.buildKey(this.productId, selectionsAbove, `focus_${normalized}`);
        const cached = this.cache.get(cacheKey);

        if (cached) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CACHE HIT for focus options:', cached.length, 'options');
            this.log('Smart Cascade: Using cached focus options for', normalized);

            // Store current value before updating
            const currentValue = select.value;
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Current value before update:', currentValue);

            this.updateDropdownOptions(normalized, cached, false);

            // Restore value if still valid
            if (currentValue) {
                const isStillValid = cached.some(opt => (opt.value || opt.slug) === currentValue);
                if (isStillValid) {
                    select.value = currentValue;
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Restored current value:', currentValue);
                } else {
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Current value NO LONGER VALID');
                }
            }

            return;
        }

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CACHE MISS - loading fresh options');

        // Race condition prevention: Generate unique request ID
        // This ensures we ignore stale responses from rapid focus/blur cycles
        this.focusRequestCounter++;
        const thisRequestId = this.focusRequestCounter;

        // Mark as loading (track which attribute AND which request)
        this.focusLoadingAttribute = normalized;

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Loading options based on ABOVE selections ONLY');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Request ID:', thisRequestId);
        this.log('Smart Cascade: Loading options for', normalized, 'based on above selections:', selectionsAbove, '(request #' + thisRequestId + ')');

        // Emit loading event
        this.emit('optionsLoading', {
            attribute: normalized,
            index: index,
            selectionsAbove: selectionsAbove
        });

        // Add visual loading indicator
        select.classList.add('hvp-focus-loading');

        try {
            let options = null;

            // USE CLIENT-SIDE CASCADE IF AVAILABLE (instant, no AJAX)
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE FOCUS OPTIONS - No AJAX');
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Calling clientCascade.getOptions with ABOVE selections only:', JSON.stringify(selectionsAbove));
                options = this.clientCascade.getOptions(selectionsAbove, normalized);
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Client cascade returned:', options?.length || 0, 'options');
            } else {
                // FALLBACK: Server-side AJAX (slow)
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SERVER-SIDE FOCUS OPTIONS - Using AJAX (slow fallback)');
                const result = await this.api.loadFilteredOptions(
                    this.productId,
                    selectionsAbove,
                    normalized
                );
                options = result?.options;
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Server returned:', options?.length || 0, 'options');
            }

            // Race condition check: Verify this is still the current request
            // If user focused on another dropdown or re-focused, ignore this stale response
            if (thisRequestId !== this.focusRequestCounter) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] IGNORED: Stale response (request', thisRequestId, 'superseded by', this.focusRequestCounter, ')');
                this.log('Smart Cascade: Ignoring stale focus response for', normalized, '(request #' + thisRequestId + ' superseded by #' + this.focusRequestCounter + ')');
                return;
            }

            if (options && options.length > 0) {
                // Cache the result
                this.cache.set(cacheKey, options);

                // Store current value before updating
                const currentValue = select.value;
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Current value before update:', currentValue);

                // Update dropdown with new options
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Updating dropdown with', options.length, 'options');
                this.updateDropdownOptions(normalized, options, false);

                // Restore selection if still valid
                if (currentValue) {
                    const isStillValid = options.some(
                        opt => (opt.value || opt.slug) === currentValue
                    );

                    if (isStillValid) {
                        select.value = currentValue;
                        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Restored current value:', currentValue);
                    } else {
                        // Current value is no longer valid - clear it
                        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Current value NO LONGER VALID - should prompt user');
                        this.log('Smart Cascade: Current value no longer valid after focus refresh');
                    }
                }

                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] FOCUS OPTIONS LOADED SUCCESSFULLY');
                this.log('Smart Cascade: Loaded', options.length, 'options for', normalized);

                this.emit('optionsLoaded', {
                    attribute: normalized,
                    options: options,
                    count: options.length
                });
            } else {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] WARNING: No options returned!');
            }

        } catch (error) {
            console.error('[HVP DEBUG] ERROR in focus handler:', error);
            // Only log error if this is still the current request
            if (thisRequestId === this.focusRequestCounter) {
                console.error('[HVP] Smart Cascade focus load error:', error);

                this.emit('optionsLoadError', {
                    attribute: normalized,
                    error: error.message
                });
            }

        } finally {
            // Only clear loading state if this was the current request
            if (thisRequestId === this.focusRequestCounter) {
                this.focusLoadingAttribute = null;
                select.classList.remove('hvp-focus-loading');
            }
        }
    },
};

export default CascadeFocusMethods;
